/**
 * Mobile Viewport Manager
 * Dynamically detects and adapts to mobile browser viewport behavior
 */

interface ViewportInfo {
  isMobile: boolean;
  isIOS: boolean;
  isSafari: boolean;
  supportsVisualViewport: boolean;
  supportsEnvironmentVariables: boolean;
  bottomBarHeight: number;
  safeAreaBottom: number;
}

class MobileViewportManager {
  private viewportInfo: ViewportInfo;
  private observers: (() => void)[] = [];
  private isInitialized = false;

  constructor() {
    this.viewportInfo = this.detectViewportInfo();
    this.init();
  }

  private detectViewportInfo(): ViewportInfo {
    const userAgent = navigator.userAgent;
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
    const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !('MSStream' in window);
    const isSafari = /Safari/.test(userAgent) && !/Chrome|CriOS|EdgiOS|FxiOS/.test(userAgent);
    const supportsVisualViewport = 'visualViewport' in window;
    const supportsEnvironmentVariables = CSS.supports('padding-bottom', 'env(safe-area-inset-bottom)');

    return {
      isMobile,
      isIOS,
      isSafari,
      supportsVisualViewport,
      supportsEnvironmentVariables,
      bottomBarHeight: 0,
      safeAreaBottom: this.getSafeAreaInsetBottom()
    };
  }

  private getSafeAreaInsetBottom(): number {
    if (!this.viewportInfo?.supportsEnvironmentVariables) return 0;
    
    // Create a temporary element to measure safe area inset
    const testElement = document.createElement('div');
    testElement.style.position = 'fixed';
    testElement.style.bottom = '0';
    testElement.style.left = '0';
    testElement.style.width = '1px';
    testElement.style.height = '1px';
    testElement.style.paddingBottom = 'env(safe-area-inset-bottom)';
    testElement.style.visibility = 'hidden';
    
    document.body.appendChild(testElement);
    const computedStyle = getComputedStyle(testElement);
    const paddingBottom = computedStyle.paddingBottom;
    document.body.removeChild(testElement);
    
    return paddingBottom ? parseInt(paddingBottom, 10) || 0 : 0;
  }

  private calculateDynamicBottomBarHeight(): number {
    if (!this.viewportInfo.supportsVisualViewport || !window.visualViewport) {
      // Fallback: estimate based on device type
      if (this.viewportInfo.isIOS && this.viewportInfo.isSafari) {
        return window.innerHeight > 800 ? 85 : 75; // Estimate for different iPhone sizes
      }
      return 0;
    }

    // Use Visual Viewport API for accurate detection
    const visualViewport = window.visualViewport;
    const windowHeight = window.innerHeight;
    const visualHeight = visualViewport.height;
    const heightDifference = windowHeight - visualHeight;

    // Account for keyboard being open
    if (heightDifference > 150) {
      return 0; // Keyboard is likely open, don't add extra padding
    }

    return Math.max(0, heightDifference);
  }

  private updateCSSCustomProperties(): void {
    const bottomBarHeight = this.calculateDynamicBottomBarHeight();
    const safeAreaBottom = this.getSafeAreaInsetBottom();
    
    // Update CSS custom properties that can be used in stylesheets
    document.documentElement.style.setProperty('--mobile-bottom-bar-height', `${bottomBarHeight}px`);
    document.documentElement.style.setProperty('--safe-area-bottom', `${safeAreaBottom}px`);
    document.documentElement.style.setProperty('--dynamic-bottom-padding', `${bottomBarHeight + safeAreaBottom}px`);

    // Update viewport info
    this.viewportInfo.bottomBarHeight = bottomBarHeight;
    this.viewportInfo.safeAreaBottom = safeAreaBottom;

    // Notify observers
    this.observers.forEach(callback => callback());

    console.log('[Mobile Viewport] Updated:', {
      bottomBarHeight,
      safeAreaBottom,
      totalPadding: bottomBarHeight + safeAreaBottom,
      visualViewportHeight: window.visualViewport?.height,
      windowHeight: window.innerHeight
    });
  }

  private init(): void {
    if (this.isInitialized) return;

    // Initial update
    this.updateCSSCustomProperties();

    // Listen for viewport changes
    if (this.viewportInfo.supportsVisualViewport && window.visualViewport) {
      window.visualViewport.addEventListener('resize', () => {
        this.updateCSSCustomProperties();
      });

      window.visualViewport.addEventListener('scroll', () => {
        this.updateCSSCustomProperties();
      });
    }

    // Fallback: listen for window resize and orientation changes
    window.addEventListener('resize', () => {
      setTimeout(() => this.updateCSSCustomProperties(), 100);
    });

    window.addEventListener('orientationchange', () => {
      setTimeout(() => this.updateCSSCustomProperties(), 300);
    });

    // Listen for focus/blur events to detect keyboard
    window.addEventListener('focusin', () => {
      setTimeout(() => this.updateCSSCustomProperties(), 300);
    });

    window.addEventListener('focusout', () => {
      setTimeout(() => this.updateCSSCustomProperties(), 300);
    });

    this.isInitialized = true;
  }

  public onViewportChange(callback: () => void): () => void {
    this.observers.push(callback);
    
    // Return unsubscribe function
    return () => {
      const index = this.observers.indexOf(callback);
      if (index > -1) {
        this.observers.splice(index, 1);
      }
    };
  }

  public getViewportInfo(): ViewportInfo {
    return { ...this.viewportInfo };
  }

  public forceUpdate(): void {
    this.updateCSSCustomProperties();
  }

  public getRecommendedBottomPadding(): number {
    const { bottomBarHeight, safeAreaBottom, isIOS, isSafari } = this.viewportInfo;
    
    if (!isIOS || !isSafari) {
      return Math.max(10, safeAreaBottom + 5); // Minimal padding for non-iOS
    }

    // For iOS Safari, use dynamic detection with reasonable bounds
    const dynamicPadding = bottomBarHeight + safeAreaBottom;
    return Math.max(15, Math.min(40, dynamicPadding + 10)); // Between 15px and 40px
  }
}

// Create singleton instance
export const mobileViewportManager = new MobileViewportManager();

// Export for manual initialization if needed
export { MobileViewportManager };
export type { ViewportInfo }; 